import gql from 'graphql-tag';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

import {
  GetDocumentsMetadataQuery,
  GetDocumentsMetadataQueryVariables,
} from './__generated/GetDocumentsMetadataQuery';

export const GET_DOCUMENTS_METADATA_QUERY = gql`
  query GetDocumentsMetadataQuery(
    $documentIds: [StrictString!]!
    $entryIds: [StrictString!]!
    $worksheetIds: [StrictString!]!
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    documents(ids: $documentIds) {
      ... on DocumentNode {
        id
        editUrl
        name
      }
      ...InaccessibleInfoGraphQL
    }
    entries(ids: $entryIds) {
      ... on EntryNode {
        id
        editUrl
        name
      }
      ...InaccessibleInfoGraphQL
    }
    worksheets(ids: $worksheetIds) {
      ... on WorksheetNode {
        id
        editUrl
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const DOCUMENT_TYPES = [
  ModelTypeConstants.DOCUMENT,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.WORKSHEET,
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number];

function isValidDocumentType(documentType: ModelTypeConstants): documentType is DocumentType {
  return DOCUMENT_TYPES.some((validDocumentType) => validDocumentType === documentType);
}

type DocumentMetadata = {documentId: string; documentType: DocumentType} & (
  | {status: QueryResultType.PENDING | QueryResultType.ERROR; name: null; editUrl: null}
  | {status: QueryResultType.SUCCESS; name: string; editUrl: string}
);

/**
 * Query metadata common to several document-like/entry-like objects.
 *
 * Supported model types: entries, worksheets, and documents (i.e. steps within worksheets).
 *
 * @param documentId API ID of document, entry, or worksheet
 */
export default function useDocumentMetadata(documentId: string): DocumentMetadata {
  const documentType = APIUtil.apiIdToType(documentId);

  assert(isValidDocumentType(documentType), `${documentType} is not a supported document type.`);

  const result = useBenchlingQuery<GetDocumentsMetadataQuery, GetDocumentsMetadataQueryVariables>(
    GET_DOCUMENTS_METADATA_QUERY,
    {
      variables: {
        documentIds: documentType === ModelTypeConstants.DOCUMENT ? [documentId] : [],
        entryIds: documentType === ModelTypeConstants.ENTRY ? [documentId] : [],
        worksheetIds: documentType === ModelTypeConstants.WORKSHEET ? [documentId] : [],
      },
    }
  );

  if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
    return {status: result.type, documentId, documentType, editUrl: null, name: null};
  }

  const document = [...result.data.documents, ...result.data.entries, ...result.data.worksheets][0];

  if (isInaccessible(document)) {
    return {status: QueryResultType.ERROR, documentId, documentType, editUrl: null, name: null};
  }

  return {status: result.type, documentId, documentType, editUrl: document.editUrl, name: document.name};
}
